// D1: 自动更新弹窗 — 模态, 折叠 release notes, 立即/稍后 两按钮 (强制场景只剩立即).
//
// 触发方式:
//   - <UpdaterRoot /> 挂在 main.tsx, 启动后台 check + 渲染弹窗
//   - 用户主动 Settings → 关于 → 检查更新, 触发 manual = true 立刻 check
//
// 强制场景 (info.forced = true):
//   - 弹窗 backdrop 不可点关
//   - Esc 不响应
//   - 只显示"立即更新"主按钮, 没有"稍后再说"
//   - 标题前加 ⚠ 警示色

import { exit as appExit } from "@tauri-apps/plugin-process";
import { useCallback, useEffect, useRef, useState } from "react";

import { useI18n } from "../lib/i18n";
import {
  checkForUpdate,
  downloadAndInstall,
  isVersionPermanentlySnoozed,
  snoozeVersion,
  type UpdateInfo,
  type UpdateProgress,
} from "../lib/updater";
import {
  emitManualCheckResult,
  onManualCheck,
} from "../lib/updater-bus";

import { useDialog } from "./Dialog";

const COLD_START_DELAY_MS = 60_000;
const POLL_INTERVAL_MS = 4 * 60 * 60_000;
// D1-UX §10: 用户在 input/textarea 输入时延后弹窗 5s, 避免打断打字
const TYPING_DELAY_MS = 5_000;

export function UpdaterRoot() {
  const [info, setInfo] = useState<UpdateInfo | null>(null);
  const [installing, setInstalling] = useState(false);
  const [progress, setProgress] = useState<UpdateProgress | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const dlg = useDialog();
  const { t } = useI18n();
  const polledRef = useRef(false);

  const handleFound = useCallback((u: UpdateInfo) => {
    // 强制更新直接展示, 不被 snooze 屏蔽
    if (!u.forced && isVersionPermanentlySnoozed(u.version)) return;
    // D1-UX §10: 若用户正在 input/textarea 打字, 延迟弹窗给完整一句话留余地
    const showNow = () => {
      setInfo(u);
      setShowNotes(false);
    };
    const focused = document.activeElement as HTMLElement | null;
    const isTyping =
      focused &&
      (focused.tagName === "INPUT" ||
        focused.tagName === "TEXTAREA" ||
        focused.isContentEditable);
    if (isTyping && !u.forced) {
      window.setTimeout(showNow, TYPING_DELAY_MS);
    } else {
      showNow();
    }
  }, []);

  // 启动后冷启动检查 + 4h 轮询 + 手动 check 订阅
  useEffect(() => {
    if (polledRef.current) return;
    polledRef.current = true;

    const cold = window.setTimeout(async () => {
      const u = await checkForUpdate();
      if (u) handleFound(u);
    }, COLD_START_DELAY_MS);

    const poll = window.setInterval(async () => {
      const u = await checkForUpdate();
      if (u) handleFound(u);
    }, POLL_INTERVAL_MS);

    // 手动 check (SettingsPanel "关于" tab 按钮): 强制弹窗, 即使被 snooze 也弹
    const offManual = onManualCheck(async () => {
      try {
        const u = await checkForUpdate();
        if (u) {
          setInfo(u);
          setShowNotes(false);
          emitManualCheckResult("found");
        } else {
          emitManualCheckResult("no-update");
        }
      } catch {
        emitManualCheckResult("error");
      }
    });

    return () => {
      window.clearTimeout(cold);
      window.clearInterval(poll);
      offManual();
    };
  }, [handleFound]);

  const handleInstall = useCallback(async () => {
    if (!info) return;
    setInstalling(true);
    setProgress({ phase: "started" });
    try {
      await downloadAndInstall(info, (p) => setProgress(p));
      // relaunch 走 Tauri process plugin, 不会回到这里
    } catch (err) {
      setInstalling(false);
      setProgress(null);
      await dlg.alert({
        title: "更新失败",
        message:
          String(err).includes("signature") || String(err).includes("Signature")
            ? "签名校验失败, 更新包可能损坏或被篡改, 已中止安装。"
            : `下载或安装失败：${err}`,
      });
    }
  }, [info, dlg]);

  const handleSnooze = useCallback(() => {
    if (!info || info.forced) return;
    snoozeVersion(info.version);
    setInfo(null);
  }, [info]);

  if (!info) return null;

  const progressText = (() => {
    if (!progress) return "";
    if (progress.phase === "started") return t("updater.progress.prepare");
    if (progress.phase === "finished") return t("updater.progress.finished");
    const total = progress.total ?? 0;
    if (total > 0) {
      const pct = Math.round((progress.downloaded / total) * 100);
      return `${pct}% (${formatBytes(progress.downloaded)} / ${formatBytes(total)})`;
    }
    return formatBytes(progress.downloaded);
  })();

  return (
    <div
      className={`updater-mask${info.forced ? " updater-mask--forced" : ""}`}
      onClick={info.forced || installing ? undefined : handleSnooze}
    >
      <div
        className="updater-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="updater-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="updater-modal__head">
          <h2 id="updater-title" className="updater-modal__title">
            {info.forced
              ? t("updater.title.forced", { version: info.version })
              : t("updater.title.found", { version: info.version })}
          </h2>
          <div className="updater-modal__sub muted small">
            {t("updater.sub.current", { version: info.currentVersion })}
            {info.date && ` · ${formatDate(info.date)}`}
          </div>
        </header>

        <div className="updater-modal__body">
          <p>
            {info.forced ? t("updater.body.forced") : t("updater.body.normal")}
          </p>

          {info.body && info.body.trim() && (
            <details
              className="updater-modal__notes"
              open={showNotes}
              onToggle={(e) =>
                setShowNotes((e.currentTarget as HTMLDetailsElement).open)
              }
            >
              <summary>
                {showNotes ? t("updater.notes.hide") : t("updater.notes.show")}
              </summary>
              <pre className="updater-modal__notes-body">{info.body}</pre>
            </details>
          )}

          {installing && (
            <div className="updater-modal__progress">
              <div className="updater-modal__progress-bar">
                <div
                  className="updater-modal__progress-fill"
                  style={{
                    width:
                      progress?.phase === "progress" && progress.total
                        ? `${Math.min(100, Math.round((progress.downloaded / progress.total) * 100))}%`
                        : progress?.phase === "finished"
                          ? "100%"
                          : "8%",
                  }}
                />
              </div>
              <div className="muted small">{progressText}</div>
            </div>
          )}
        </div>

        <footer className="updater-modal__foot">
          {info.forced && !installing && (
            // D1-UX §3: 强制场景下"稍后再说"置灰可见 (用户能看到选项被剥夺), 同时给一个
            // "退出应用" 兜底, 不让断网用户被卡死
            <>
              <button
                type="button"
                className="btn btn--ghost"
                disabled
                title={t("updater.body.forced")}
              >
                {t("updater.btn.later")}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void appExit(0)}
              >
                {t("updater.btn.exit")}
              </button>
            </>
          )}
          {!info.forced && !installing && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleSnooze}
            >
              {t("updater.btn.later")}
            </button>
          )}
          <button
            type="button"
            className="btn btn--primary"
            disabled={installing}
            onClick={() => void handleInstall()}
          >
            {installing ? t("updater.btn.installing") : t("updater.btn.now")}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------- helpers ----------

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch {
    return iso;
  }
}
